import React, { useState, ChangeEvent } from 'react';
import { usePermissions, useTranslate, useGetOne, useRefresh } from 'react-admin';
import { useQuery } from 'react-query';
import Button from '@material-ui/core/Button';
import RefundIcon from '@material-ui/icons/Undo';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';

import { EditButton, SelectInput, TextInput } from '~/components';
import { HeadTransaction } from '~/types/HeadTransaction';
import TransactionResultAlert from '~/components/TransactionResultAlert';
import { getTransaction } from '~/api/headTransactions';
import { check, createConsent, createTransaction } from '~/api/transactionRefundRejected';
import { amountToString } from '~/utils/amountFormat';
import DetailsTab from '../DetailsTab';

interface Props {
  transaction: HeadTransaction;
  history?: any;
}

type DialogState = 'CLOSED' | 'FORM' | 'PROGRESS' | 'PREVIEW' | 'SUCCESS' | 'ERROR';
const DELAY_MS = 2000;

const CANDIDATE_OPERATION_TYPES = ['REMITTANCE', 'BILL_PAYMENT', 'PAYOUT'];

const RefundRejectedAction = (props: Props) => {
  const { transaction, history } = props;
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string, args?: any) =>
    translate(`resources.headTransactions.show.details.refundRejected.${key}`, args);
  const classes = useStyles();

  const [dialogState, setDialogState] = useState<DialogState>('CLOSED');
  const [error, setError] = useState<string>();
  const [recipientAccountId, setRecipientAccountId] = useState<string>('');
  const [reasonValue, setReasonValue] = useState<string>('');
  const [consent, setConsent] = useState<HeadTransaction>();
  const [refundTransaction, setRefundTransaction] = useState<HeadTransaction>();

  const forceRefresh = useRefresh();

  const hasPermission = !!permissions?.includes('headTransaction.refundRejected');

  const isRefunded = !!transaction.details.references?.some(
    (reference) => reference.referenceType === 'REFUND_TRANSACTION'
  );
  const isCandidate =
    transaction.status === 'REJECTED' &&
    CANDIDATE_OPERATION_TYPES.includes(transaction.operationType) &&
    !isRefunded;

  // we need to wait for resource loading in order to eliminate unnecessary request after forced component update
  const { loading } = useGetOne('headTransactions', transaction.id);
  const needToCheckRefund = isCandidate && hasPermission && !loading;

  const { data: checkResponse, refetch: refetchCheck } = useQuery(
    ['checkRefundRejected', transaction.id],
    () => check({ transactionId: transaction.id }),
    {
      enabled: needToCheckRefund,
      onError: () => {},
      retry: false,
    }
  );
  const checkResult = checkResponse?.data;

  const isRefundAvailable = needToCheckRefund && !!checkResult?.available;

  const showError = (e: any) => {
    const errorText = e?.body?.message || 'Unknown error occurred';
    setError(errorText);
    setDialogState('ERROR');
  };

  const onOpen = () => {
    setError(undefined);
    setConsent(undefined);
    setRefundTransaction(undefined);
    setReasonValue('');
    setRecipientAccountId(checkResult?.recipientAccounts?.[0]?.id || '');
    setDialogState('FORM');
  };

  const onClose = () => {
    if (dialogState === 'PROGRESS') return;
    if (dialogState === 'SUCCESS' || dialogState === 'ERROR') {
      forceRefresh();
      refetchCheck();
    }
    setDialogState('CLOSED');
  };

  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    setReasonValue(event.target.value);
  };

  const onPreview = () => {
    setDialogState('PROGRESS');
    createConsent({ transactionId: transaction.id, recipientAccountId, reason: reasonValue })
      .then(({ data }) => {
        setConsent(data);
        setDialogState('PREVIEW');
      })
      .catch(showError);
  };

  const checkForTransaction = (pendingTransaction: HeadTransaction) => {
    getTransaction({ id: pendingTransaction.id })
      .then(({ data }) => {
        setRefundTransaction(data);
        setDialogState('SUCCESS');
      })
      .catch(() => {
        setTimeout(() => checkForTransaction(pendingTransaction), DELAY_MS);
      });
  };

  const onConfirm = () => {
    if (!consent) return;
    setDialogState('PROGRESS');
    createTransaction({ consentId: consent.id })
      .then(({ data }) => {
        setTimeout(() => checkForTransaction(data), DELAY_MS);
      })
      .catch(showError);
  };

  if (!isRefundAvailable) {
    return null;
  }

  const accountOptions = (checkResult?.recipientAccounts || []).map((account) => ({
    value: account.id,
    text: account.alias ? `${account.number} (${account.alias})` : account.number,
  }));

  return (
    <>
      <EditButton onClick={onOpen} label={t('button')} icon={<RefundIcon />} red />
      <Dialog
        open={dialogState !== 'CLOSED'}
        onClose={onClose}
        aria-labelledby='refund-rejected-dialog-title'
        fullWidth
        maxWidth={dialogState === 'PREVIEW' ? 'lg' : dialogState === 'FORM' ? 'sm' : 'xs'}
      >
        <DialogTitle id='refund-rejected-dialog-title'>{t('dialogTitle')}</DialogTitle>
        <DialogContent>
          {dialogState === 'PROGRESS' && (
            <Box mt={1} mb={3}>
              <LinearProgress />
            </Box>
          )}
          {dialogState === 'ERROR' && <Alert severity='error'>{error}</Alert>}
          {dialogState === 'SUCCESS' && refundTransaction && (
            <TransactionResultAlert transaction={refundTransaction} successMessage={t('success')} />
          )}
          {dialogState === 'FORM' && (
            <>
              <Alert severity='warning'>
                {t('warning', {
                  amount: checkResult?.amount ? amountToString(checkResult.amount) : '???',
                  source: transaction.details.senderExternalSource?.type || '???',
                })}
              </Alert>
              <Box mt={2}>
                <SelectInput
                  label={t('recipientAccount')}
                  name='recipientAccountId'
                  value={recipientAccountId}
                  options={accountOptions}
                  onChange={(e) => setRecipientAccountId(e.target.value)}
                  className={classes.fullWidth}
                  required
                />
              </Box>
              <Box mt={2}>
                <TextInput
                  value={reasonValue}
                  onChange={handleReasonChange}
                  multiline
                  rows={5}
                  rowsMax={20}
                  required
                  fullWidth
                  label={t('reason')}
                />
              </Box>
            </>
          )}
          {dialogState === 'PREVIEW' && consent && <DetailsTab record={consent} readOnly />}
        </DialogContent>

        {dialogState !== 'PROGRESS' && (
          <DialogActions>
            {dialogState === 'FORM' && (
              <>
                <Button onClick={onClose}>{t('cancel')}</Button>
                <Button
                  onClick={onPreview}
                  color='primary'
                  disabled={!recipientAccountId || reasonValue.trim().length === 0}
                >
                  {t('preview')}
                </Button>
              </>
            )}
            {dialogState === 'PREVIEW' && (
              <>
                <Button onClick={onClose}>{t('cancel')}</Button>
                <Button onClick={onConfirm} color='primary'>
                  {t('confirm')}
                </Button>
              </>
            )}
            {(dialogState === 'SUCCESS' || dialogState === 'ERROR') && (
              <Button onClick={onClose}>{t('close')}</Button>
            )}
            {dialogState === 'SUCCESS' && refundTransaction && (
              <Button
                onClick={() => {
                  history && history.push(`/headTransactions/${refundTransaction.id}/show`);
                }}
                color='primary'
              >
                {t('goToTransaction')}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

const useStyles = makeStyles(() => ({
  fullWidth: {
    width: '100%',
  },
}));

export default RefundRejectedAction;
